import React, { useState } from 'react';
import './ContactUs.css';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/email-forward/forward-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setError(result.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Contact form error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="contact-us-page">
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%)',
        color: 'white',
        padding: 'var(--spacing-16) 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ fontSize: 'var(--font-size-4xl)', marginBottom: 'var(--spacing-6)' }}>
            Contact Us
          </h1>
          <p style={{ 
            fontSize: 'var(--font-size-xl)', 
            maxWidth: '600px',
            margin: '0 auto',
            color: 'white',
            fontWeight: '500',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}>
            Have questions? We&apos;re here to help! Get in touch with our support team.
          </p>
        </div>
      </section>

      <div className="container" style={{ padding: 'var(--spacing-8) 0' }}>
        <div className="row">
          {/* Contact Form */}
          <div className="col col--6">
            <div className="card p-6">
              <h2 style={{ marginBottom: 'var(--spacing-6)', color: 'var(--primary-blue)' }}>
                Send us a Message
              </h2>
              
              {submitted ? (
                <div className="text-center p-6">
                  <div className="bg-success text-white" style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--font-size-2xl)',
                    margin: '0 auto var(--spacing-4)'
                  }}>
                    ✓
                  </div>
                  <h3>Message Sent!</h3>
                  <p className="text-muted">
                    Thank you for contacting us. We&apos;ll get back to you within 24 hours.
                  </p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="btn btn--primary"
                    style={{ marginTop: 'var(--spacing-4)' }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {error && (
                    <div className="alert alert--danger" style={{ marginBottom: 'var(--spacing-4)' }}>
                      {error}
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Subject *</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="form-input"
                      required
                      disabled={loading}
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="safety">Safety Concern</option>
                      <option value="feature">Feature Request</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input"
                      rows="5"
                      required
                      placeholder="Tell us how we can help you..."
                      disabled={loading}
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="btn btn--primary"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info & FAQ */}
          <div className="col col--6">
            {/* Contact Information */}
            <div className="card p-6" style={{ marginBottom: 'var(--spacing-6)' }}>
              <h3 style={{ marginBottom: 'var(--spacing-4)', color: 'var(--primary-blue)' }}>
                Get in Touch
              </h3>
              
              <div className="contact-info">
                <div className="contact-item">
                  <div className="contact-icon">📧</div>
                  <div>
                    <strong>Email</strong><br />
                    <a href="mailto:support@gradedwash.com">support@gradedwash.com</a>
                    <br />
                    <small style={{ color: '#666' }}>
                      (Automatically forwarded to admin.sally@gmail.com)
                    </small>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">⏰</div>
                  <div>
                    <strong>Response Time</strong><br />
                    Within 24 hours
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">🌍</div>
                  <div>
                    <strong>Service Hours</strong><br />
                    24/7 Support Available
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="card p-6">
              <h3 style={{ marginBottom: 'var(--spacing-4)', color: 'var(--primary-blue)' }}>
                Quick Links
              </h3>
              
              <div className="quick-links">
                <a href="/about" className="quick-link">
                  <span>ℹ️</span>
                  <span>About Us</span>
                </a>
                <a href="/terms-of-service" className="quick-link">
                  <span>📋</span>
                  <span>Terms of Service</span>
                </a>
                <a href="/privacy-policy" className="quick-link">
                  <span>🔒</span>
                  <span>Privacy Policy</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section" style={{ marginTop: 'var(--spacing-8)' }}>
          <h2 className="text-center" style={{ marginBottom: 'var(--spacing-6)' }}>
            Frequently Asked Questions
          </h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How does personality matching work?</h4>
              <p>
                Our advanced algorithm analyzes your responses to our comprehensive personality quiz, 
                considering factors like travel style, communication preferences, and interests. 
                We then match you with compatible travelers who share similar values and travel goals.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>Is GradedWash safe to use?</h4>
              <p>
                Yes! We prioritize your safety with verified profiles, secure messaging, 
                and comprehensive safety guidelines. All users must complete identity verification, 
                and we have a dedicated safety team monitoring the platform.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>How much does it cost to use GradedWash?</h4>
              <p>
                Basic features are free! You can create a profile, take the personality test, 
                and browse potential matches. Premium features like unlimited messaging, 
                local meets, and travel groups require a subscription starting at £8.99/month.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>Can I cancel my subscription anytime?</h4>
              <p>
                Absolutely! You can cancel your subscription at any time through your account settings. 
                You&apos;ll continue to have access to premium features until the end of your current billing period.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>What if I have a bad experience with a match?</h4>
              <p>
                We take all reports seriously. If you have concerns about a user, 
                please report them immediately through our safety reporting system. 
                Our team will investigate and take appropriate action.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>How do I organize a local meet?</h4>
              <p>
                Simply go to the &quot;Local Meets&quot; section, click &quot;Create a Local Meet&quot;, 
                and fill in the details like location, date, and activity. 
                Other users in your area can then join your meetup!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 